import './BreadCrump.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Breadcrumb } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { translationKeys } from '@/config/breadcrumbConfig';
import { getEntityKeyValueList } from '@/core/infrastructure/Commons';
import { ACCOUNT_ITEM_KEY, getActivePath } from '@/shared/layout/navigation';
import {
  createTranslationTable,
  formatUrlName,
  formatUrlPath,
  toTitleCase,
} from '@/shared/utils/breadcrumbUtils';
import MenuItems from '@/shared/utils/SideMenuItems';

// Detail routes that live under another sidebar item.
const MENU_PATH_ALIASES = {
  'my-channels': '/mychannels',
  'cms-channels': '/cms',
  'cms-management': '/cms',
};

const isGuid = (str) => {
  const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidPattern.test(str);
};

const BreadCrumb = () => {
  const { t } = useTranslation(['breadcrumb', 'sidebar-menu']);
  const menuItems = MenuItems(t);
  const location = useLocation();
  const params = useParams();
  const { name } = params;
  const [entityNames, setEntityNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingEntities, setLoadingEntities] = useState(new Set());
  const previousParameterKey = useRef('');

  // Get all route parameter values to filter them out from breadcrumb
  const routeParamValues = Object.values(params).filter(Boolean);

  // Filter out path segments that match route parameters (dynamic IDs)
  const pathSegments = location.pathname
    .split('/')
    .filter(Boolean)
    .filter((segment) => !routeParamValues.includes(segment));

  const searchParams = new URLSearchParams(location.search);
  const channelId = searchParams.get('channelId');
  const cmsId = searchParams.get('cmsId');

  // Track specific parameter changes to avoid unnecessary API calls
  const parameterKey = useMemo(() => {
    return `${cmsId || ''}-${channelId || ''}-${pathSegments.join('-')}`;
  }, [cmsId, channelId, location.pathname]);

  const translationTable = createTranslationTable(menuItems);

  const buildUrlWithParams = (basePath, additionalParams = {}) => {
    const params = new URLSearchParams();

    if (cmsId) params.set('cmsId', cmsId);
    if (channelId) params.set('channelId', channelId);

    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const buildUrlWithSpecificParams = (basePath, allowedParams = []) => {
    const params = new URLSearchParams();

    if (allowedParams.includes('cmsId') && cmsId) {
      params.set('cmsId', cmsId);
    }
    if (allowedParams.includes('channelId') && channelId) {
      params.set('channelId', channelId);
    }

    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const getContextAwareUrl = (segment, basePath) => {
    if (segment === 'my-channels') {
      return '/mychannels';
    } else if (segment === 'cms-channels') {
      return buildUrlWithSpecificParams('/cms-management/channels', ['cmsId']);
    } else {
      return buildUrlWithParams(basePath);
    }
  };

  useEffect(() => {
    const fetchEntityNames = async () => {
      setLoading(true);
      const names = {};
      const entitiesToLoad = new Set();

      // Add entities that need to be loaded to the loading state
      if (cmsId && isGuid(cmsId)) {
        entitiesToLoad.add(cmsId);
      }
      if (channelId && isGuid(channelId)) {
        entitiesToLoad.add(channelId);
      }
      for (let i = 0; i < pathSegments.length; i++) {
        const segment = pathSegments[i];
        if (isGuid(segment) && !entityNames[segment]) {
          entitiesToLoad.add(segment);
        }
      }

      // Set loading state immediately
      setLoadingEntities(entitiesToLoad);

      try {
        if (cmsId && isGuid(cmsId)) {
          try {
            const cmsResponse = await getEntityKeyValueList({
              index: 0,
              size: 10,
              ids: cmsId,
              entityType: 'Cms',
              serviceType: 'channel-service',
            });

            if (cmsResponse?.body?.items?.length > 0) {
              names[cmsId] = cmsResponse.body.items[0].name;
            }
            entitiesToLoad.delete(cmsId);
          } catch (error) {
            console.error('Error fetching CMS name:', error);
            entitiesToLoad.delete(cmsId);
          }
        }

        if (channelId && isGuid(channelId)) {
          try {
            const channelResponse = await getEntityKeyValueList({
              index: 0,
              size: 10,
              ids: channelId,
              entityType: 'Channel',
              serviceType: 'channel-service',
            });

            if (channelResponse?.body?.items?.length > 0) {
              names[channelId] = channelResponse.body.items[0].name;
            }
            entitiesToLoad.delete(channelId);
          } catch (error) {
            console.error('Error fetching Channel name:', error);
            entitiesToLoad.delete(channelId);
          }
        }

        for (let i = 0; i < pathSegments.length; i++) {
          const segment = pathSegments[i];
          if (isGuid(segment) && !names[segment]) {
            try {
              let entityType = 'Channel';
              let serviceType = 'channel-service';

              if (pathSegments[0] === 'cms' && i === 1) {
                entityType = 'Cms';
              }

              const response = await getEntityKeyValueList({
                index: 0,
                size: 10,
                ids: segment,
                entityType: entityType,
                serviceType: serviceType,
              });

              if (response?.body?.items?.length > 0) {
                names[segment] = response.body.items[0].name;
              }
              entitiesToLoad.delete(segment);
            } catch (error) {
              console.error(`Error fetching ${segment} name:`, error);
              entitiesToLoad.delete(segment);
            }
          }
        }
      } catch (error) {
        console.error('Error in fetchEntityNames:', error);
      }

      setEntityNames(names);
      setLoadingEntities(entitiesToLoad);
      setLoading(false);
    };

    const shouldFetchNames =
      (channelId && isGuid(channelId)) ||
      (cmsId && isGuid(cmsId)) ||
      pathSegments.some((segment) => isGuid(segment));

    if (shouldFetchNames && parameterKey !== previousParameterKey.current) {
      previousParameterKey.current = parameterKey;
      fetchEntityNames();
    }
  }, [parameterKey]);

  // The sidebar group the current page lives in (e.g. Tools) is shown as plain text: groups have no page.
  const navigationItems = menuItems.filter((item) => item.key !== ACCOUNT_ITEM_KEY);
  const activeMenuPath = getActivePath(location.pathname, navigationItems);
  const menuGroup = navigationItems.find((item) =>
    item.children?.some((child) => child.path === activeMenuPath)
  );
  const homeLabel = toTitleCase(t(translationKeys.home));

  const breadcrumbItems = [
    {
      title: (
        <Link to={buildUrlWithParams('/')} className="c-breadcrumb__home" title={homeLabel}>
          {t(translationKeys.brand)}
        </Link>
      ),
      key: 'home',
    },
    ...(menuGroup
      ? [
          {
            title: <span className="c-breadcrumb__group">{menuGroup.label}</span>,
            key: `group-${menuGroup.key}`,
          },
        ]
      : []),
    ...pathSegments.map((segment, index) => {
      const decodedSegment = decodeURIComponent(segment);
      const basePath = `/${pathSegments.slice(0, index + 1).join('/')}`;
      const isChannelName = decodedSegment === name;

      let formattedSegment;
      let linkUrl;

      if (isGuid(decodedSegment)) {
        formattedSegment =
          entityNames[decodedSegment] ||
          (loadingEntities.has(decodedSegment) ? (
            <span className="inline-flex items-center gap-1">
              <LoadingOutlined className="text-xs animate-spin" />
            </span>
          ) : (
            <span className="c-breadcrumb__muted">{t('common:common.unknown')}</span>
          ));
      } else if (isChannelName) {
        formattedSegment = formatUrlName(name);
      } else {
        formattedSegment =
          translationTable[decodedSegment.toLowerCase()] ||
          t(translationKeys[decodedSegment.toLowerCase()] || '') ||
          t(`breadcrumb.${decodedSegment}`, { defaultValue: decodedSegment }) ||
          formatUrlName(decodedSegment);
      }

      // A segment repeating the sidebar group shown before it (e.g. Facebook) is left out.
      if (
        menuGroup &&
        index < pathSegments.length - 1 &&
        typeof formattedSegment === 'string' &&
        formattedSegment.toLocaleLowerCase('tr-TR') ===
          String(menuGroup.label).toLocaleLowerCase('tr-TR')
      ) {
        return null;
      }

      linkUrl = getContextAwareUrl(segment, basePath);

      const isLastSegment = index === pathSegments.length - 1;
      const isSecondLastSegment = index === pathSegments.length - 2;
      const shouldBeDisabled = isLastSegment || isSecondLastSegment;

      return {
        title: shouldBeDisabled ? (
          <span className={isLastSegment ? 'c-breadcrumb__current' : 'c-breadcrumb__disabled'}>
            {typeof formattedSegment === 'string'
              ? toTitleCase(formattedSegment.replaceAll('-', ' '))
              : formattedSegment}
          </span>
        ) : (
          <Link to={linkUrl}>
            {typeof formattedSegment === 'string'
              ? toTitleCase(formattedSegment.replaceAll('-', ' '))
              : formattedSegment}
          </Link>
        ),
        key: linkUrl,
      };
    }),
  ].filter(Boolean);

  const dynamicItems = [];

  if (cmsId && entityNames[cmsId] && !pathSegments.includes(cmsId)) {
    const cmsTargetUrl = buildUrlWithSpecificParams('/cms-management/channels', ['cmsId']);

    dynamicItems.push({
      title: (
        <Link to={cmsTargetUrl} className="c-breadcrumb__link">
          {entityNames[cmsId]}
        </Link>
      ),
      key: `cms-${cmsId}`,
    });
  } else if (cmsId && isGuid(cmsId) && loadingEntities.has(cmsId)) {
    dynamicItems.push({
      title: (
        <span className="inline-flex items-center gap-1">
          <LoadingOutlined className="text-xs animate-spin" />
        </span>
      ),
      key: `cms-${cmsId}`,
    });
  }

  if (channelId && entityNames[channelId] && !pathSegments.includes(channelId)) {
    dynamicItems.push({
      title: <span className="c-breadcrumb__disabled">{entityNames[channelId]}</span>,
      key: `channel-${channelId}`,
    });
  } else if (channelId && isGuid(channelId) && loadingEntities.has(channelId)) {
    dynamicItems.push({
      title: (
        <span className="inline-flex items-center gap-1">
          <LoadingOutlined className="text-xs animate-spin" />
        </span>
      ),
      key: `channel-${channelId}`,
    });
  }

  // A page that belongs to a sidebar item reads "group / item" like the sidebar (tabs and route
  // parts such as contents/videos are left out); a record opened from it follows the item.
  const accountPages = menuItems.find((item) => item.key === ACCOUNT_ITEM_KEY)?.children ?? [];
  const sidebarPath = getActivePath(
    MENU_PATH_ALIASES[pathSegments[0]] ?? location.pathname,
    navigationItems
  );
  const sidebarPage =
    navigationItems
      .flatMap((item) => (item.children?.length ? item.children : [item]))
      .find((item) => item.path === sidebarPath) ??
    accountPages.find((item) => item.path === location.pathname);
  const sidebarGroup = navigationItems.find((item) =>
    item.children?.some((child) => child.path === sidebarPage?.path)
  );
  const sidebarGroupLabel =
    sidebarGroup?.label ??
    (sidebarPage && !accountPages.includes(sidebarPage)
      ? t('sidebar-menu:sidebar-menu.modules')
      : null);
  // A channel opened from Kanallarım reads "Kanallarım / channel"; its CMS is a step only on the
  // Kanal Yönetim Sistemleri path.
  const recordItems = dynamicItems
    .filter((item) => !(pathSegments[0] === 'my-channels' && item.key.startsWith('cms-')))
    .map((item, index, items) =>
      index === items.length - 1 && item.key.startsWith('channel-') && entityNames[channelId]
        ? {
            ...item,
            title: <span className="c-breadcrumb__current">{entityNames[channelId]}</span>,
          }
        : item
    );

  const sidebarBreadcrumbItems = sidebarPage && [
    breadcrumbItems[0],
    ...(sidebarGroupLabel
      ? [
          {
            title: <span className="c-breadcrumb__group">{sidebarGroupLabel}</span>,
            key: 'sidebar-group',
          },
        ]
      : []),
    {
      title: recordItems.length ? (
        <Link to={sidebarPage.path}>{sidebarPage.label}</Link>
      ) : (
        <span className="c-breadcrumb__current">{sidebarPage.label}</span>
      ),
      key: sidebarPage.path,
    },
    ...recordItems,
  ];

  const finalBreadcrumbItems = sidebarBreadcrumbItems || [
    ...breadcrumbItems.slice(0, -1),
    ...dynamicItems,
    ...breadcrumbItems.slice(-1),
  ];

  return (
    <div className="c-breadcrumb">
      <Breadcrumb className="c-breadcrumb__trail" separator="/" items={finalBreadcrumbItems} />
    </div>
  );
};

export default BreadCrumb;
